import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import {
  LayoutDashboard, CalendarDays, Clock, Users, MapPin, Wallet, Landmark, TrendingUp,
  TrendingDown, BarChart3, FileText, Package, Star, History, Database, Image as ImageIcon,
  Search, Bell, BellRing, ChevronDown, ChevronLeft, Menu, X, Plus, Settings, LogOut, Trash2,
  CalendarRange, UserCog, ListChecks, Repeat, CalendarOff, Receipt, Gift, Flag, ChevronRight,
  Banknote, ClipboardCheck, Grid3X3
} from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_17ae65fc-a3ab-44cd-ac8e-52289b6992ff/artifacts/m6249hnu_Sar%C4%B1%20Kurumsal%20Hukuk%20Dan%C4%B1%C5%9Fmanl%C4%B1%C4%9F%C4%B1%20Fatura.png';

// Menü yapısı — SADECE mevcut modüller. tab: Tabs value'su, perm: personel izni
const buildMenu = ({ isPersonnel, hasPerm }) => {
  const groups = [
    {
      id: 'general', label: 'Genel Yönetim', items: [
        { tab: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true, testid: 'nav-dashboard' },
        { tab: 'calendar', label: 'Takvim', icon: CalendarDays, show: hasPerm('takvim'), testid: 'nav-calendar' },
        { tab: 'notifications', label: 'Bildirimler', icon: Bell, show: !isPersonnel || hasPerm('bildirimler'), testid: 'nav-notifications' },
        { tab: 'reminders', label: 'Hatırlatmalar', icon: BellRing, show: true, testid: 'nav-reminders' },
      ],
    },
    {
      id: 'operation', label: 'Operasyon Yönetimi', items: [
        { tab: 'organizations', label: 'Organizasyonlar', icon: CalendarRange, show: hasPerm('org_view'), testid: 'nav-organizations' },
        { tab: 'reservations', label: 'Rezervasyonlar', icon: Clock, show: hasPerm('rezervasyonlar'), testid: 'nav-reservations' },
        { tab: 'salons', label: 'Salon ve Tesisler', icon: MapPin, show: hasPerm('salonlar'), testid: 'nav-salons' },
        { tab: 'occupancy', label: 'Saha Dolum Raporu', icon: Grid3X3, show: hasPerm('salonlar') || hasPerm('rezervasyonlar'), testid: 'nav-occupancy' },
      ],
    },
    {
      id: 'finance', label: 'Finans Yönetimi', testid: 'nav-accounting-dropdown', items: [
        { tab: 'financials', label: 'Kasa ve Banka', icon: Wallet, show: hasPerm('kasa'), testid: 'nav-financials' },
        { tab: 'cash-accounts', label: 'Kasalar', icon: Banknote, show: hasPerm('kasa') || hasPerm('kasa_hesap'), testid: 'nav-cash-accounts' },
        { tab: 'closing', label: 'Gün Sonu Kontrol', icon: ClipboardCheck, show: hasPerm('gunsonu'), testid: 'nav-closing' },
        { tab: 'financials', sub: 'fixed', label: 'Sabit Giderler', icon: Receipt, show: hasPerm('kasa'), testid: 'nav-fixed-expenses' },
        { tab: 'vat', label: 'KDV Hesaplama', icon: FileText, show: !isPersonnel, testid: 'nav-vat' },
        { tab: 'referees', label: 'Hakemler', icon: Flag, show: hasPerm('hakemler'), testid: 'nav-referees' },
        { tab: 'bonuses', label: 'Primler', icon: Gift, show: !isPersonnel, testid: 'nav-bonuses' },
        { tab: 'analytics', label: 'Finansal Raporlar', icon: BarChart3, show: hasPerm('analiz'), testid: 'nav-reports' },
      ],
    },
    {
      id: 'hr', label: 'İnsan Kaynakları', testid: 'nav-hr-dropdown', items: [
        { tab: 'staff', label: 'Personel', icon: UserCog, show: hasPerm('personel'), testid: 'nav-personnel' },
        { tab: 'mesai', label: 'Mesai', icon: Clock, show: !isPersonnel || hasPerm('mesai') || hasPerm('personel'), testid: 'nav-mesai' },
        { tab: 'tasks-overview', label: 'Görevler', icon: ListChecks, show: hasPerm('gorevler'), testid: 'nav-tasks' },
        { tab: 'routines', label: 'Rutinler', icon: Repeat, show: !isPersonnel || hasPerm('rutinler') || hasPerm('gorevler'), testid: 'nav-routines' },
        { tab: 'izinler', label: 'İzinler', icon: CalendarOff, show: !isPersonnel || hasPerm('mesai') || hasPerm('personel'), testid: 'nav-leaves' },
      ],
    },
    {
      id: 'corporate', label: 'Kurumsal Yönetim', items: [
        { tab: 'customers', label: 'Müşteriler ve Kurumlar', icon: Users, show: hasPerm('musteriler'), testid: 'nav-customers' },
        { tab: 'demirbas', label: 'Demirbaş', icon: Package, show: !isPersonnel || hasPerm('demirbas'), testid: 'nav-demirbas' },
        { tab: 'feedback', label: 'Geri Bildirimler', icon: Star, show: !isPersonnel || hasPerm('geribildirim'), testid: 'nav-feedback' },
        { tab: 'etkinlik', label: 'Etkinlikler (Site)', icon: ImageIcon, show: !isPersonnel, testid: 'nav-events' },
        { tab: 'blog', label: 'Blog Yazıları', icon: FileText, show: !isPersonnel, testid: 'nav-blog' },
      ],
    },
    {
      id: 'system', label: 'Sistem', items: [
        { tab: 'activity-log', label: 'İşlem Geçmişi', icon: History, show: !isPersonnel || hasPerm('islemkayitlari'), testid: 'nav-activity-log' },
        { tab: 'database', label: 'Veritabanı', icon: Database, show: !isPersonnel, testid: 'nav-database' },
      ],
    },
  ];
  return groups
    .map(g => ({ ...g, items: g.items.filter(i => i.show) }))
    .filter(g => g.items.length > 0);
};

export const AdminShell = ({
  activeTab, onNavigate, currentUser, isPersonnel, hasPerm, onLogout,
  onOpenSettings, onResetData, onQuickAction, notificationCount = 0, children,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const menu = useMemo(() => buildMenu({ isPersonnel, hasPerm }), [isPersonnel, hasPerm]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    if (!q) return menu;
    return menu
      .map(g => ({ ...g, items: g.items.filter(i => i.label.toLocaleLowerCase('tr').includes(q)) }))
      .filter(g => g.items.length > 0);
  }, [menu, query]);

  const activeGroup = menu.find(g => g.items.some(i => i.tab === activeTab));
  const activeItem = activeGroup?.items.find(i => i.tab === activeTab);

  useEffect(() => {
    if (activeGroup) setOpenGroups(prev => ({ ...prev, [activeGroup.id]: true }));
  }, [activeGroup?.id]); // eslint-disable-line

  const go = (item) => {
    onNavigate(item.tab, item.sub);
    setMobileOpen(false);
  };

  // Masaüstünde gruplar açık; mobil çekmecede yalnızca aktif grup açık (daha az kaydırma)
  const isOpen = (g, mobile = false) => {
    if (query) return true;
    if (openGroups[g.id] !== undefined) return openGroups[g.id];
    return mobile ? g.id === activeGroup?.id : true;
  };

  const renderSidebar = (mobile = false) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-2.5 px-4 py-4 border-b border-white/10 ${collapsed ? 'justify-center px-2' : ''}`}>
        <div className="bg-white rounded-lg p-1 shrink-0">
          <img src={LOGO} alt="Avrasya" className="h-7 w-7 object-contain" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-bold text-white leading-tight truncate">AVRASYA</p>
            <p className="text-[10px] tracking-[0.18em] text-sky-300/80 font-medium">SPORTİF FAALİYETLER</p>
          </div>
        )}
      </div>

      {/* Menüde ara */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Menüde ara..."
              data-testid="sidebar-search"
              className="w-full h-8 pl-8 pr-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
          </div>
        </div>
      )}

      {/* Menü */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1" data-testid="admin-sidebar-nav">
        {filtered.map(g => (
          <div key={g.id} className="pb-1">
            {!collapsed && (
              <button
                onClick={() => setOpenGroups(prev => ({ ...prev, [g.id]: !isOpen(g, mobile) }))}
                data-testid={g.testid || `nav-group-${g.id}`}
                className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors"
              >
                {g.label}
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen(g, mobile) ? '' : '-rotate-90'}`} />
              </button>
            )}
            {(collapsed || isOpen(g, mobile)) && g.items.map(item => {
              const active = activeTab === item.tab && (!item.sub || activeItem?.sub === item.sub);
              return (
                <button
                  key={item.testid || `${item.tab}-${item.sub || ''}`}
                  onClick={() => go(item)}
                  data-testid={item.testid}
                  title={collapsed ? item.label : undefined}
                  className={`w-full flex items-center gap-2.5 rounded-lg transition-colors duration-150 mb-0.5
                    ${collapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'}
                    ${active ? 'bg-[#176B91] text-white shadow-sm' : 'text-slate-300 hover:bg-white/8 hover:text-white'}`}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span className="text-[13px] font-medium truncate">{item.label}</span>}
                </button>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && !collapsed && (
          <p className="text-xs text-slate-500 px-3 py-4">Sonuç bulunamadı</p>
        )}
      </nav>

      <div className="border-t border-white/10 p-3">
        {!collapsed ? (
          <div className="flex items-center gap-2 text-slate-400">
            <div className="bg-white/10 rounded-lg p-1.5"><LayoutDashboard className="w-3.5 h-3.5" /></div>
            <div className="min-w-0">
              <p className="text-[11px] text-slate-300 font-medium truncate">Avrasya Sportif</p>
              <p className="text-[10px] text-slate-500">Yönetim Paneli</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const quickActions = [
    { key: 'organization', label: 'Yeni Organizasyon', cls: 'bg-[#176B91] hover:bg-[#145a7a] text-white', testid: 'quick-new-organization' },
    { key: 'income', label: 'Gelir Ekle', cls: 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200', testid: 'quick-add-income' },
    { key: 'expense', label: 'Gider Ekle', cls: 'bg-rose-50 text-rose-800 hover:bg-rose-100 border border-rose-200', testid: 'quick-add-expense' },
    { key: 'reservation', label: 'Rezervasyon', cls: 'bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-200', testid: 'quick-new-reservation' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col shrink-0 bg-[#0B172A] border-r border-black/20 transition-all duration-200 ${collapsed ? 'w-[68px]' : 'w-[248px]'} sticky top-0 h-screen`}>
        {renderSidebar(false)}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-[262px] bg-[#0B172A] z-50 lg:hidden" data-testid="mobile-sidebar">
            <button onClick={() => setMobileOpen(false)} className="absolute right-2 top-2 text-slate-400 p-1.5" data-testid="mobile-sidebar-close"><X className="w-4 h-4" /></button>
            {renderSidebar(true)}
          </aside>
        </>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2 px-3 md:px-5 py-2.5">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            <button onClick={() => setCollapsed(c => !c)} className="hidden lg:flex p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="sidebar-toggle" title="Menüyü daralt/genişlet">
              <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
            </button>

            <div className="relative flex-1 max-w-md hidden sm:block">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); if (e.target.value) setMobileOpen(false); }}
                placeholder="Sayfa ve modül ara... (muhasebe, organizasyon, personel)"
                className="pl-9 h-9 bg-[#F8FAFC] border-[#E2E8F0] text-sm"
                data-testid="global-search"
              />
              {query && (
                <div className="absolute left-0 right-0 top-11 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-1.5 max-h-72 overflow-y-auto z-40" data-testid="global-search-results">
                  {filtered.length === 0 ? <p className="text-xs text-slate-500 px-2 py-2">Sonuç yok</p> : filtered.flatMap(g => g.items.map(i => (
                    <button key={g.id + (i.testid || i.tab)} onClick={() => { go(i); setQuery(''); }}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-slate-100 text-left">
                      <i.icon className="w-4 h-4 text-[#176B91]" />
                      <span className="text-[13px] text-slate-800">{i.label}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{g.label}</span>
                    </button>
                  )))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 ml-auto">
              {!isPersonnel && quickActions.map(a => (
                <Button key={a.key} onClick={() => onQuickAction(a.key)} size="sm"
                  className={`h-9 hidden xl:inline-flex shadow-none ${a.cls}`} data-testid={a.testid}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> {a.label}
                </Button>
              ))}
              {!isPersonnel && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className="h-9 xl:hidden bg-[#176B91] hover:bg-[#145a7a]" data-testid="quick-actions-mobile">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {quickActions.map(a => (
                      <DropdownMenuItem key={a.key} onClick={() => onQuickAction(a.key)}>{a.label}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <button onClick={() => onNavigate('notifications')} className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="topbar-notifications">
                <Bell className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                {notificationCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#DC2626] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{notificationCount}</span>
                )}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-lg hover:bg-slate-100" data-testid="topbar-user-menu">
                    <div className="w-8 h-8 rounded-full bg-[#162B49] text-white flex items-center justify-center text-xs font-semibold">
                      {(currentUser?.username || 'A').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-[13px] font-semibold text-slate-800 leading-tight">{currentUser?.username || 'admin'}</p>
                      <p className="text-[10px] text-slate-500">{isPersonnel ? 'Personel' : 'Yönetici'}</p>
                    </div>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-xs">{currentUser?.username}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {!isPersonnel && (
                    <DropdownMenuItem onClick={onOpenSettings} data-testid="menu-account-settings">
                      <Settings className="w-4 h-4 mr-2 text-slate-600" /> Hesap Ayarları
                    </DropdownMenuItem>
                  )}
                  {!isPersonnel && (
                    <DropdownMenuItem onClick={onResetData} className="text-rose-600" data-testid="menu-reset-data">
                      <Trash2 className="w-4 h-4 mr-2" /> Verileri Sıfırla
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2 text-slate-600" /> Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 px-4 md:px-5 pb-2 text-[11px] text-slate-500" data-testid="breadcrumb">
            <span>Yönetim Paneli</span>
            {activeGroup && <><ChevronRight className="w-3 h-3" /><span>{activeGroup.label}</span></>}
            {activeItem && <><ChevronRight className="w-3 h-3" /><span className="text-slate-800 font-medium">{activeItem.label}</span></>}
            <span className="ml-auto hidden sm:block">
              {now.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' })}
            </span>
          </div>
        </header>

        <main className="flex-1 p-3 md:p-5 max-w-full overflow-x-hidden" data-testid="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
